/* ============================================================
   HIT: movimento. Uma função, mover(), e o carregamento do Motion só onde ele é usado.
   Portado de projetos/design-propostas/direcao-b/index.html ("Movimento", mover). Mudança contra a B, declarada: a B quebrava se o
   elemento fosse nulo (a guarda vinha depois do uso); aqui a guarda vem primeiro.

     window.HitMovimento = { mover, carregaMotion, prepara, MOTION_URL, MOTION_SRI, PRAZO_MS }

   mover(el, de, para, o): anima as propriedades CSS de `de` (opcional; sem ele parte do estilo calculado) até `para`.
     o = { mola, rig, amort, dur, ease, atraso }: com mola:true usa mola (rigidez 380, amortecimento 34); senão duração (0,2 s) e curva.
     Devolve { stop(), finished: Promise }. Ao terminar, `para` fica gravado no style do elemento.
     stop() deixa a animação onde estava (não grava o estado final) e resolve `finished`.
   Motion 12 (jsDelivr) quando carregou; senão Web Animations (WAAPI), o que o navegador já tem. Com prefers-reduced-motion: reduce NADA anima:
     o estado final é aplicado na hora e o Motion nem é baixado.

   MOTION FIXADO. O arquivo é a versão exata (motion@12.43.0) e vai com integridade (SRI sha384, crossorigin anonymous): se o jsDelivr servir
   outra coisa, o navegador recusa o script e o tema anima pelas Web Animations. Subir a versão é trocar MOTION_URL e MOTION_SRI juntos
   (hash: curl -s <url> | openssl dgst -sha384 -binary | openssl base64 -A, com o prefixo "sha384-").

   QUANDO BAIXAR. O Motion só é baixado onde há faixa desdobrada ou parede de raquetes (botão [data-act="faixa"], [data-faixa], .parede), em hora
   ociosa, para não pesar no carregamento: prepara() só olha o que já existe quando a página carrega.
   >>> Todo JS que desenhar uma faixa ou parede DEPOIS DO CARREGAMENTO (blocos que nascem de busca, filtro, "ver mais") tem de chamar
   >>> HitMovimento.carregaMotion() antes de animar o que desenhou; senão a animação sai pelas Web Animations (funciona, só não é a do Motion).
   carregaMotion(prazoMs) devolve uma Promise que resolve com o Motion, ou com null se não veio (bloqueado, offline, SRI recusado) ou se não veio
   dentro do prazo DESTA chamada (3 s; PRAZO_MS). O download, porém, é um só por página: o prazo vencido não cancela nem repete nada (quem espera
   cai nas Web Animations, e o arquivo segue baixando para as chamadas seguintes); se o download falha (bloqueio, SRI recusado), a tag morta sai e
   a página não tenta de novo (um pedido, uma execução e, no máximo, um erro no console).
   Carregar o arquivo duas vezes não faz nada na segunda.
   ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined' || window.HitMovimento) return;

  const MOTION_URL = 'https://cdn.jsdelivr.net/npm/motion@12.43.0/dist/motion.js';
  const MOTION_SRI = 'sha384-hgVFh5YKDMdpcWEjpRqk2kPXOKBH7I9NBCKe3uR6dHKgy/BWPe8kyL01kiYuRn1u';
  const PRAZO_MS = 3000;
  const REDUZ = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ONDE_USA = '[data-act="faixa"], [data-faixa], .parede';
  const PARADO = Object.freeze({ stop() {}, finished: Promise.resolve() });
  let baixando = null; // a Promise do download: criada uma vez por página; resolve com o Motion ou com null (falha definitiva)

  function baixa() {
    if (!baixando) {
      baixando = new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = MOTION_URL;
        s.integrity = MOTION_SRI;
        s.crossOrigin = 'anonymous';
        s.async = true;
        s.onload = () => resolve(window.Motion || null);
        s.onerror = () => { s.remove(); resolve(null); }; // bloqueado, offline ou SRI recusado: sem tag morta e sem nova tentativa
        document.head.appendChild(s);
      });
    }
    return baixando;
  }
  /* Promise do Motion; null se não veio em `prazo` ms (o padrão é PRAZO_MS). Só o prazo é de cada chamada; o download é o de baixa() */
  function carregaMotion(prazo) {
    if (window.Motion && window.Motion.animate) return Promise.resolve(window.Motion);
    let timer;
    const vencido = new Promise((resolve) => { timer = setTimeout(resolve, prazo == null ? PRAZO_MS : prazo, null); });
    return Promise.race([baixa(), vencido]).then((m) => { clearTimeout(timer); return m; });
  }
  function prepara() {
    if (REDUZ.matches || !document.querySelector(ONDE_USA)) return Promise.resolve(null);
    return carregaMotion();
  }

  function mover(el, de, para, o) {
    o = o || {};
    if (!el) return PARADO;
    if (REDUZ.matches) { Object.assign(el.style, para); return PARADO; }
    const kf = {};
    Object.keys(para).forEach((k) => { kf[k] = [de && de[k] != null ? de[k] : window.getComputedStyle(el)[k], para[k]]; });
    try {
      if (window.Motion && window.Motion.animate) {
        const op = o.mola
          ? { type: 'spring', stiffness: o.rig || 380, damping: o.amort || 34, delay: o.atraso || 0 }
          : { duration: o.dur || 0.2, ease: o.ease || [0.16, 0.1, 0.3, 1], delay: o.atraso || 0 };
        const ctl = window.Motion.animate(el, kf, op);
        let parado = false, encerra;
        const fim = new Promise((r) => { encerra = r; if (ctl.then) ctl.then(r, r); else r(); });
        fim.then(() => { if (!parado) Object.assign(el.style, para); }); // parada por stop() fica onde estava: o estado final não é gravado
        /* o Motion não resolve a promessa da animação parada; resolvemos aqui, como as Web Animations fazem ao cancelar */
        return { stop() { parado = true; try { ctl.stop(); } catch (e) { /* já terminou */ } encerra(); }, finished: fim };
      }
    } catch (e) { /* o Motion falhou: cai nas Web Animations */ }
    const frames = [0, 1].map((i) => { const f = {}; Object.keys(kf).forEach((k) => { f[k] = kf[k][i]; }); return f; });
    const a = el.animate(frames, { duration: (o.dur || 0.2) * 1000 * (o.mola ? 1.6 : 1), delay: (o.atraso || 0) * 1000, easing: 'cubic-bezier(.16,.1,.3,1)', fill: 'both' });
    const fim = a.finished.then(() => { Object.assign(el.style, para); a.cancel(); }, () => {});
    return {
      stop() {
        /* commitStyles grava o ponto atual no style; lança se o elemento não está renderizado (display:none), e aí só cancela */
        try { a.commitStyles(); } catch (e) { /* elemento sem renderização: não há ponto a gravar */ }
        a.cancel();
      },
      finished: fim,
    };
  }

  window.HitMovimento = Object.freeze({ mover, carregaMotion, prepara, MOTION_URL, MOTION_SRI, PRAZO_MS });
  const quandoOcioso = window.requestIdleCallback || ((f) => setTimeout(f, 200));
  const inicia = () => quandoOcioso(prepara);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', inicia); else inicia();
})();
